import React from "react";
import { Article, ArticleType, ArticleList, ArticleFieldChange, fieldValidation } from "../../../../../amorg-common";
import { articlesClientApi } from "../../../../../api/articlesClientApi";
import { Flex } from "../../../../../designSystem/compositeComponents/FlexHelpers";
import { SimpleFieldChangePanel, TextFieldChangePanel } from "../../../../../designSystem/FieldChangePanel";
import { DateInputField } from "../../../../../designSystem/formField/DateInputField";
import { FormField } from "../../../../../designSystem/formField/FormField";
import { SelectField } from "../../../../../designSystem/formField/SelectField";
import { TextSaveField } from "../../../../../designSystem/formField/text/TextSaveField";
import { LoadingPageOverlay } from "../../../../../designSystem/LoadingPageOverlay";
import { P } from "../../../../../designSystem/typography/Body";
import { toDateString } from "../../../../../utils/date";
import { ArticleViewImportanceSelector } from "./ArticleViewImportanceSelector";
import { HtmlEditorWithAutoSave } from "../../../../../designSystem/inputs/HtmlEditorWithAutoSave";

type ArticleViewFieldsProps = {
    oldVersion: Article | null,
    newVersion: Article,
    latestVersion: boolean,
    types: ArticleType[],
    lists: ArticleList[],
    onChanged: () => Promise<void>;
};
export function ArticleViewFields (props: ArticleViewFieldsProps): JSX.Element
{
    const [loading, setLoading] = React.useState(false);

    async function handleUpdateField (change: ArticleFieldChange):Promise<boolean>
    {
        setLoading(true);
        const result = await articlesClientApi.updateArticleField(props.newVersion.articleId, change);
        if(result.status === 'success')
            await props.onChanged();

        setLoading(false);
        return result.status === 'success';
    }

    async function handleUpdateDateField (value: Date | null)
    {
        setLoading(true);
        await articlesClientApi.updateArticleField(props.newVersion.articleId, { field: 'date', value });
        await props.onChanged();
        setLoading(false);
    }

    const oldVersion = props.oldVersion;
    const newVersion = props.newVersion;
    const disabled = props.newVersion.release !== null;

    const tagReplacementToggleTip =
        <span>
            <P verticalMargin="8px" fontSize="14px">Der kan benyttes følgende erstatningstekst i feltet:</P>

            <P verticalMargin="8px" fontSize="14px">
                <b>{'{company.name}'}</b><br />
                Indsætter organisationens fulde navn hvis defineret. Ellers organisationens id.
            </P>
        </span>;

    return (
        <Flex flexDirection="column" gap={16}>
            <LoadingPageOverlay loading={loading} />

            <div>
                <TextSaveField
                    disabled={disabled}
                    label="Titel"
                    value={props.newVersion.title}
                    validator={(value: string) =>
                        fieldValidation.article.title(value)
                            ? { valid: true }
                            : { valid: false, errorMessage: `Skal udfyldes. Maks. 200 (${value.length}) tegn` }
                    }
                    onSave={(value) => handleUpdateField({ field: 'title', value })} />
                {oldVersion &&
                    <SimpleFieldChangePanel oldValue={oldVersion.title} newValue={newVersion.title} />
                }
            </div>

            <div>
                <TextSaveField
                    disabled={disabled}
                    label='Lovnummer'
                    value={props.newVersion.reference}
                    onSave={(value) => handleUpdateField({ field: 'reference', value })} />
                {oldVersion &&
                    <SimpleFieldChangePanel oldValue={oldVersion.reference} newValue={newVersion.reference} />
                }
            </div>

            <div>
                <TextSaveField
                    disabled={disabled}
                    label='Webadresse'
                    value={props.newVersion.url}
                    onSave={(value) => handleUpdateField({ field: 'url', value })} />
                <Flex justifyContent='flex-end'>
                    <a href={
                        props.newVersion.url.toLowerCase().startsWith('www')
                            ? `http://${props.newVersion.url}`
                            : props.newVersion.url
                    }
                        target='_blank'
                        rel='noreferrer'>
                        Gå til webadressen
                    </a>
                </Flex>
                {oldVersion &&
                    <SimpleFieldChangePanel oldValue={oldVersion.url} newValue={newVersion.url} />
                }
            </div>

            <div>
                <SelectField label='Liste/emne'
                    disabled={disabled}
                    selectedValue={props.newVersion.topic.id}
                    onChange={(value) => handleUpdateField({ field: 'topicId', value })}
                    options={props.lists.map(l =>
                        ({ text: l.name, options: l.topics.map(t => ({ text: t.name, value: t.id })) }))}
                />
            </div>

            <div>
                <SelectField label='Type lovkrav'
                    disabled={disabled}
                    selectedValue={props.newVersion.type.id}
                    onChange={(value) => handleUpdateField({ field: 'typeId', value })}
                    options={props.types.map(t => ({ text: t.name, value: t.id }))}
                />
            </div>

            <div>
                <DateInputField
                    disabled={props.newVersion.release !== null}
                    label='Lov udgivet'
                    value={props.newVersion.date}
                    onValueChange={handleUpdateDateField}
                    validator={() => { return { valid: true }; }}
                    showClearButton
                />
                {oldVersion &&
                    <SimpleFieldChangePanel
                        oldValue={oldVersion.date
                            ? toDateString(oldVersion.date)
                            : ''}
                        newValue={newVersion.date
                            ? toDateString(newVersion.date)
                            : ''
                        } />
                }
            </div>

            <div>
                <FormField
                    label="Beskrivelse"
                    inputComponent={
                        <HtmlEditorWithAutoSave
                            id={newVersion.articleVersionId + 'description_editor'}
                            readonly={disabled}
                            value={props.newVersion.description}
                            onSave={(value) => handleUpdateField({ field: 'description', value })}
                        />
                    }
                />
                {oldVersion &&
                    <TextFieldChangePanel
                        diffMode='words'
                        oldText={oldVersion.description}
                        newText={newVersion.description} />
                }
            </div>

            {newVersion.release === null &&
                <div>
                    <ArticleViewImportanceSelector
                        groupName='importance'
                        importance={props.newVersion.importance}
                        onImportanceChange={(importance) => handleUpdateField({ field: 'importance', value: importance })}
                    />
                </div>
            }

            <FormField
                label="Ændringsbeskrivelse"
                toolTip={tagReplacementToggleTip}
                inputComponent={
                    <HtmlEditorWithAutoSave
                        id={newVersion.articleVersionId + 'change_editor'}
                        readonly={disabled}
                        value={props.newVersion.changeDescription}
                        onSave={(value) => handleUpdateField({ field: 'changeDescription', value })}
                    />
                }
            />

            <FormField
                label="Vurdering"
                toolTip={tagReplacementToggleTip}
                inputComponent={
                    <HtmlEditorWithAutoSave
                        id={newVersion.articleVersionId + 'assessment_editor'}
                        readonly={disabled}
                        value={props.newVersion.assessment}
                        onSave={(value) => handleUpdateField({ field: 'assessment', value })}
                    />
                }
            />

            {props.latestVersion &&
                <FormField
                    label="Juridisk forpligtigelse"
                    toolTip={
                        <>
                            <P verticalMargin="8px" fontSize="14px">
                                Bemærk at feltet juridisk forpligtigelse er fælles for alle versioner af et lovkrav og derfor <u>kan opdateres uden at oprette en ny version!</u>.
                            </P>
                            <P verticalMargin="8px" fontSize="14px">
                                Da feltet kan overskrives af en kunde, vil ændringer desuden ikke fremgå i forbindelse med udgivelse af nye versioner.
                            </P>
                            {tagReplacementToggleTip}
                        </>
                    }
                    inputComponent={
                        <HtmlEditorWithAutoSave
                            id={newVersion.articleVersionId + 'requirements_editor'}
                            value={props.newVersion.requirements}
                            onSave={(value) => handleUpdateField({ field: 'requirements', value })}
                        />
                    }
                />
            }

        </Flex>
    );
}